import { Badge, Button, Card, Heading, Text } from "@components/theming";
import { TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { EventShowing } from "@types";
import { joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface Props {
  showingcomboconfig: {
    connectedShowings: number[];
    maxShowings: number;
    minShowings: number;
  };
  activeShowings: EventShowing[];
  children: (selectedConnectedShowings: number[]) => React.ReactElement;
}

const selected = {
  hide: {
    height: 0,
    marginBottom: 0,
    transition: {
      delay: 0.2,
    },
  },
  show: {
    height: "auto",
    marginBottom: "1.25rem",
  },
};

const container = {
  hide: {
    opacity: 0,
  },
  show: {
    opacity: 1,
    transition: {
      delayChildren: 0.4,
      staggerChildren: 0.05,
    },
  },
};

const item = {
  hide: {
    opacity: 0,
    x: 5,
  },
  show: {
    opacity: 1,
    x: 0,
  },
};

export default function ConnectedShowings({
  showingcomboconfig,
  activeShowings,
  children,
}: Props) {
  const [selectedConnectedShowings, setSelectedConnectedShowings] = useState<
    number[]
  >(
    showingcomboconfig.connectedShowings.length ===
      showingcomboconfig.minShowings
      ? showingcomboconfig.connectedShowings
      : []
  );
  const minSelectsRemaining =
    showingcomboconfig.minShowings - selectedConnectedShowings.length;
  const maxSelectsRemaining =
    showingcomboconfig.maxShowings - selectedConnectedShowings.length;

  const t = useTranslations("EventPage.connectedshowings");

  return (
    <div className="flex flex-col">
      <motion.div
        variants={selected}
        initial="hide"
        animate={selectedConnectedShowings.length > 0 ? "show" : "hide"}
        className="flex flex-col gap-2"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={
            selectedConnectedShowings.length > 0
              ? { opacity: 1, transition: { delay: 0.2 } }
              : { opacity: 0 }
          }
          className="flex items-center justify-between"
        >
          <Heading
            as="h2"
            weight="bold"
            size="4"
            shopsiteClasses="text-lg font-semibold text-black truncate"
          >
            {showingcomboconfig.connectedShowings.length !==
            showingcomboconfig.minShowings
              ? t("selectedshowings")
              : t("selectedshowingsnochoice")}
          </Heading>
          {showingcomboconfig.connectedShowings.length !==
            showingcomboconfig.minShowings && (
            <AnimatePresence>
              <Button
                color="red"
                whitelabelVariant="soft"
                onClick={() => setSelectedConnectedShowings([])}
                shopsiteClasses="gap-1 py-1 !rounded-lg bg-red-100 !text-red-800 hover:bg-red-500 hover:!text-white"
              >
                <TrashIcon className="h-5 w-5" />
                <Text
                  as="span"
                  weight="medium"
                  shopsiteClasses="font-semibold text-base"
                >
                  {t("clear")}
                </Text>
              </Button>
            </AnimatePresence>
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={
            selectedConnectedShowings.length > 0
              ? { opacity: 1, transition: { delay: 0.2 } }
              : { opacity: 0 }
          }
          className="flex items-center gap-1 flex-wrap"
        >
          {showingcomboconfig.connectedShowings.map((showing_id) => {
            const connectedShowing = activeShowings.find(
              (showing) => showing.id === showing_id
            );
            if (!connectedShowing) {
              return <></>;
            }
            return (
              <AnimatePresence key={showing_id}>
                {selectedConnectedShowings.includes(showing_id) && (
                  <Badge asChild size="3" shopsiteClasses="!rounded-lg !py-1">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Text as="span" shopsiteClasses="text-sm font-medium">
                        {connectedShowing.name || connectedShowing.date.display}
                      </Text>
                      {showingcomboconfig.connectedShowings.length !==
                      showingcomboconfig.minShowings ? (
                        <button
                          onClick={() => {
                            setSelectedConnectedShowings((prev) =>
                              prev.filter((id) => id !== showing_id)
                            );
                          }}
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      ) : (
                        <></>
                      )}
                    </motion.div>
                  </Badge>
                )}
              </AnimatePresence>
            );
          })}
        </motion.div>
      </motion.div>
      <motion.div layout="position">
        <Heading
          as="h2"
          asChild
          size="3"
          shopsiteClasses="text-lg font-semibold"
          sharedClasses="truncate"
        >
          <motion.h2
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={
              maxSelectsRemaining > 0
                ? {
                    opacity: 1,
                    height: "auto",
                    marginBottom: "1.25rem",
                    transition: { opacity: { delay: 0.2 } },
                  }
                : {
                    opacity: 0,
                    height: 0,
                    marginBottom: 0,
                    transition: {
                      height: { delay: 0.2 },
                      marginBottom: { delay: 0.2 },
                    },
                  }
            }
            transition={{ delay: 0.2 }}
          >
            {t("chooseshowing", {
              maxRemaining: Math.max(maxSelectsRemaining, 1),
              numShowings: selectedConnectedShowings.length,
            })}
          </motion.h2>
        </Heading>
        <AnimatePresence>
          {maxSelectsRemaining > 0 && (
            <motion.div
              initial={{ opacity: 1, height: "auto" }}
              animate={{
                opacity: 1,
                height: "auto",
                marginBottom: minSelectsRemaining > 0 ? 0 : "2rem",
              }}
              exit={{ opacity: 0, height: 0 }}
            >
              <motion.div
                variants={container}
                initial="hide"
                animate="show"
                className="isolate grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6"
              >
                {showingcomboconfig.connectedShowings.map((showingId) => {
                  const connectedShowing = activeShowings.find(
                    (showing) => showing.id === showingId
                  );
                  if (!connectedShowing) {
                    return <></>;
                  }
                  return (
                    <Card
                      asChild
                      key={showingId}
                      sharedClasses={joinClassNames(
                        maxSelectsRemaining === 0 ||
                          selectedConnectedShowings.includes(showingId)
                          ? "hidden"
                          : "block",
                        "cursor-pointer"
                      )}
                      shopsiteClasses="relative rounded-md border shadow border-gray-100 transition-colors ease-in-out duration-200 px-4 py-2"
                    >
                      <motion.button
                        variants={item}
                        onClick={() =>
                          setSelectedConnectedShowings((prev) =>
                            !prev.includes(showingId) && minSelectsRemaining > 0
                              ? [...prev, showingId]
                              : prev.includes(showingId)
                              ? prev.filter((id) => id !== showingId)
                              : prev
                          )
                        }
                      >
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex flex-col overflow-hidden justify-center text-base font-medium w-full">
                            <Text
                              as="p"
                              weight="medium"
                              sharedClasses="text-left truncate"
                            >
                              {connectedShowing?.name}
                            </Text>
                            <Text
                              as="span"
                              weight="medium"
                              sharedClasses="text-left text-sm w-fit"
                              whitelabelClasses="text-[color:var(--accent-11)]"
                              shopsiteClasses="text-indigo-500"
                            >
                              {connectedShowing?.date.display
                                ? connectedShowing.date.display
                                : connectedShowing?.date.local.formatted}
                            </Text>
                          </div>
                        </div>
                      </motion.button>
                    </Card>
                  );
                })}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {minSelectsRemaining === 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              {children(selectedConnectedShowings)}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
